<template>
  <div class="region-selector flex flex-wrap items-center gap-4">
    <vs-select v-if="modelValue.region" autocomplete :value="modelValue.region.id" :disabled="readonly" @input="onSelectRegion">
      <vs-select-item v-for="region in regions" :key="`${uid}_${region.id}`" :value="region.id" :text="region.name" />
    </vs-select>

    <vs-select autocomplete v-model="selectZone1" :disabled="readonly || !(modelValue.region ? modelValue.region.id : false)">
      <vs-select-item v-for="zone in zoneDepth1" :key="`${uid}_${zone.id}`" :value="zone.id" :text="zone.name" />
    </vs-select>

    <vs-select v-if="zoneDepth2.length > 0" autocomplete v-model="selectZone2" :disabled="readonly || !(modelValue.region ? modelValue.region.id : false)">
      <vs-select-item v-for="zone in zoneDepth2" :key="`${uid}_${zone.id}`" :value="zone.id" :text="zone.name" />
    </vs-select>

    <slot name="custom"></slot>
  </div>
</template>

<script>
import HttpRequest from "@/util/HttpRequest";

// 캐싱을 위한 Map 객체 생성
const promiseCache = new Map();

/**
 * 캐시 키 생성 함수
 * @param {string} actionName - 액션 이름
 * @param {*} params - 파라미터
 * @returns {string} 캐시 키
 */
function getCacheKey(actionName, params) {
  return `${actionName}:${JSON.stringify(params)}`;
}

/**
 * API 호출 결과를 캐싱하는 함수
 * @param {string} cacheKey - 캐시 키
 * @param {Function} apiCallFn - API 호출 함수
 * @param {number} memCacheTime - 메모리 캐싱 시간 (밀리초), 기본값 10분
 * @param {boolean} useLocalStorage - 로컬 스토리지 사용 여부, 기본값 true
 * @param {number} localStorageTime - 로컬 스토리지 캐싱 시간 (밀리초), 기본값 5분
 * @returns {Promise} API 호출 결과를 담은 Promise
 */
async function cachedApiCall(cacheKey, apiCallFn, memCacheTime = 600000, useLocalStorage = true, localStorageTime = 300000) {
  // 1. 먼저 메모리 캐시 확인
  if (promiseCache.has(cacheKey)) {
    return promiseCache.get(cacheKey);
  }

  // 2. 로컬 스토리지 캐시 확인 (사용하는 경우)
  if (useLocalStorage) {
    const lsKey = `ls_cache_${cacheKey}`;
    const cached = localStorage.getItem(lsKey);

    if (cached) {
      try {
        const { data, expiry } = JSON.parse(cached);
        // 만료 시간 확인
        if (expiry > Date.now()) {
          // 유효한 캐시가 있으면 Promise로 래핑하여 반환
          const cachedPromise = Promise.resolve(data);
          promiseCache.set(cacheKey, cachedPromise);
          return cachedPromise;
        } else {
          // 만료된 캐시는 삭제
          localStorage.removeItem(lsKey);
        }
      } catch (e) {
        // JSON 파싱 오류 시 캐시 삭제
        localStorage.removeItem(lsKey);
      }
    }
  }

  // 3. 캐시 없음, API 호출 실행
  const promise = apiCallFn()
    .then((response) => {
      // 로컬 스토리지에 저장 (사용하는 경우)
      if (useLocalStorage) {
        try {
          const lsKey = `ls_cache_${cacheKey}`;
          const cacheData = {
            data: response,
            expiry: Date.now() + localStorageTime,
          };
          localStorage.setItem(lsKey, JSON.stringify(cacheData));
        } catch (e) {
          // 저장 실패 시 무시 (로컬 스토리지 크기 초과 등의 이유)
          console.warn("Failed to cache in localStorage:", e);
        }
      }
      return response;
    })
    .finally(() => {
      // 메모리 캐시 만료 처리
      setTimeout(() => {
        promiseCache.delete(cacheKey);
      }, memCacheTime);
    });

  // 메모리 캐시에 저장 후 반환
  promiseCache.set(cacheKey, promise);
  return promise;
}

export default {
  name: "RegionSelector",
  props: {
    value: [Object, String],
    readonly: Boolean,
  },
  computed: {
    modelValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    zoneDepth1() {
      if (this.zones && this.zones.length > 0) {
        return this.zones.filter((zone) => !zone.parent_zone_id);
      }
      return [];
    },
    zoneDepth2() {
      // parent_zone_id 가 selectZone1 인 항목을 표시

      if (this.selectZone1 && this.zones && this.zones.length > 0) {
        return this.zones.filter((zone) => zone.parent_zone_id === this.selectZone1);
      }
      return [];
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        if (typeof value !== "object") {
          value = {
            region: { id: 0, name: "" },
            zone: { id: 0, name: "" },
          };
        }
        value.region = value.region || { id: 0, name: "" };
        value.zone = value.zone || { id: 0, name: "" };

        this.modelValue = value;
      },
    },
    "modelValue.region": {
      immediate: true,
      deep: true,
      handler(value) {
        if (value && value.id) {
          this.loadRegionDetail(value.id);
        }
      },
    },
    selectZone1: {
      immediate: true,
      deep: true,
      handler(zoneId) {
        if (zoneId) {
          const children = this.zones.filter((zone) => zone.parent_zone_id === zoneId);
          if (children.length === 0) {
            this.onSelectZone(zoneId);
          } else {
            const findChildren = children.find((zone) => zone.id === this.selectZone2);
            if (!findChildren) {
              this.selectZone2 = children[0].id;
            }
          }
        }
      },
    },
    selectZone2: {
      immediate: true,
      deep: true,
      handler(zoneId) {
        if (zoneId) {
          this.onSelectZone(zoneId);
        }
      },
    },
  },
  data() {
    return {
      uid: Math.random().toString(36).substr(2, 9),
      regions: [],
      zones: [],

      selectZone1: null,
      selectZone2: null,
    };
  },
  mounted() {
    this.loadRegionList();
  },
  methods: {
    loadRegionList() {
      const option = {
        params: {
          page: 1,
          perPage: 20,
          keyword: "", // 검색이 필요할 경우 사용,
        },
      };
      // keyword 를 사용하지 않을 경우 삭제
      if (option.params.keyword === "") {
        delete option.params.keyword;
      }

      const api = "/api/v1/region";
      HttpRequest.get(api, option)
        .then((response) => {
          const data = response.data;
          this.regions = data;
        })
        .catch(() => {});
    },

    async loadRegionDetail(id) {
      const cacheKey = getCacheKey("loadRegionDetail", { id });

      const response = await cachedApiCall(cacheKey, () => {
        const api = `/api/v1/region/${id}`;
        return new Promise((resolve, reject) => {
          HttpRequest.get(api, { params: { id } })
            .then((response) => {
              this.zones = response[0].zones || [];

              const find = this.zones.find((zone) => zone.id === this.value.zone.id);
              if (find) {
                if (find.parent_zone_id) {
                  this.selectZone2 = find.id;
                  this.selectZone1 = find.parent_zone_id;
                } else {
                  this.selectZone1 = find.id;
                }
              }

              resolve(response);
            })
            .catch((error) => {
              console.log("Error loading region detail:", error);
              reject(error);
            });
        });
      });

      if (response) {
        this.zones = response[0].zones || [];

        const find = this.zones.find((zone) => zone.id === this.value.zone.id);

        if (find) {
          if (find.parent_zone_id) {
            this.selectZone2 = find.id;
            this.selectZone1 = find.parent_zone_id;
          } else {
            this.selectZone1 = find.id;
          }
        }
      }
    },

    setRegionWithName(regionName) {
      const find = this.regions.find((region) => region.name === regionName);
      if (find) {
        this.modelValue = {
          ...this.modelValue,
          region: {
            id: find.id,
            name: find.name,
          },
        };
      }
    },

    onSelectRegion(regionId) {
      this.selectZone2 = null;
      this.selectZone1 = null;
      const find = this.regions.find((region) => region.id === regionId);
      if (find) {
        this.modelValue = {
          ...this.modelValue,
          region: {
            id: find.id,
            name: find.name,
          },
        };
        this.loadRegionDetail(find.id);
      }
    },

    onSelectZone(zoneId) {
      const find = this.zones.find((zone) => zone.id === zoneId);
      if (find) {
        this.modelValue = {
          ...this.modelValue,
          zone: {
            id: find.id,
            name: find.name,
          },
        };
      }
    },
  },
};
</script>

<style scoped></style>
